/**
 * Small status pill shown in the admin sidebar footer. Silent when
 * everything is fine; goes amber ("reconnecting…") when axios detects
 * a failing request, and red ("offline") when the OS declares us down.
 *
 * Placement decision: the sidebar footer is the only spot visible on
 * every admin/booth page without stealing space from primary content.
 * On mobile (sidebar drawer collapsed) we render it as a floating
 * bottom-right chip so the DJ still sees it during a set.
 */
import React from 'react';
import { Wifi, WifiOff, Loader2 } from 'lucide-react';
import { useConnectivity } from '../hooks/useConnectivity';

const CONFIG = {
  online: {
    label: 'Online',
    Icon: Wifi,
    text: 'text-[#39FF14]',
    bg: 'bg-[#39FF14]/10 border-[#39FF14]/20',
    dot: 'bg-[#39FF14]',
  },
  reconnecting: {
    label: 'Reconnecting…',
    Icon: Loader2,
    text: 'text-[#FFB020]',
    bg: 'bg-[#FFB020]/10 border-[#FFB020]/30',
    dot: 'bg-[#FFB020]',
    spin: true,
  },
  offline: {
    label: 'Offline',
    Icon: WifiOff,
    text: 'text-[#FF3B3B]',
    bg: 'bg-[#FF3B3B]/10 border-[#FF3B3B]/30',
    dot: 'bg-[#FF3B3B]',
  },
};

export const ConnectivityPill = ({ variant = 'sidebar' }) => {
  const status = useConnectivity();
  const cfg = CONFIG[status] || CONFIG.online;
  const { Icon, spin } = cfg;

  // In the sidebar we render a full pill; when 'online' we still show a
  // tiny green pulse so the DJ knows the app is alive during long idle
  // stretches (matches the "is it still working?" reflex).
  if (variant === 'sidebar') {
    return (
      <div
        data-testid={`connectivity-pill-${status}`}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-[10px] uppercase tracking-widest font-semibold ${cfg.bg} ${cfg.text}`}
        title={status === 'reconnecting' ? 'The last request failed — retrying' : cfg.label}
      >
        <Icon className={`w-3 h-3 ${spin ? 'animate-spin' : ''}`} />
        <span>{cfg.label}</span>
      </div>
    );
  }

  // Mobile / floating variant — hidden when online to stay out of the way.
  if (status === 'online') return null;
  return (
    <div
      data-testid={`connectivity-pill-floating-${status}`}
      className={`fixed bottom-4 right-4 z-[9999] flex items-center gap-2 px-3 py-2 rounded-full border shadow-lg backdrop-blur-md ${cfg.bg} ${cfg.text}`}
    >
      <Icon className={`w-3.5 h-3.5 ${spin ? 'animate-spin' : ''}`} />
      <span className="text-xs font-semibold">{cfg.label}</span>
    </div>
  );
};

export default ConnectivityPill;
